import React from 'react';
import './Integration.css';

const Integration = ({ selectedProduct, onContactClick }) => {
  if (selectedProduct === 'core') {
    return <CoreSection onContactClick={onContactClick} />;
  }
  if (selectedProduct === 'bi') {
    return <BISection onContactClick={onContactClick} />;
  }

  return <ETLSection onContactClick={onContactClick} />;
};

/* ========================================
   Core Section – Platform overview, rollout, CTA
   ======================================== */
const CoreSection = ({ onContactClick }) => {
  const rolloutSteps = [
    {
      step: '01',
      title: 'Master Data',
      description: 'Set up partners, products, categories, and addresses — your single source of truth.',
      icon: '📋'
    },
    {
      step: '02',
      title: 'Pricing & Sales',
      description: 'Import supplier costs, configure margin rules, and start capturing sales orders with AI imports.',
      icon: '💰'
    },
    {
      step: '03',
      title: 'Invoicing & Warehouse',
      description: 'Issue branded invoices, enable receiving and outbound, and deploy the floor PWA for pick and pack.',
      icon: '📦'
    },
    {
      step: '04',
      title: 'Network & Integrations',
      description: 'Connect trading partners on Neoshift Network and push approved documents to Accurate Online.',
      icon: '🔗'
    }
  ];

  const modules = [
    { name: 'AI Chat', description: 'Ask questions in plain English — get SQL-backed answers and charts scoped to your company' },
    { name: 'Accounts Payable', description: 'Supplier invoice upload with three-way match against PO and goods receipt' },
    { name: 'Accurate Online', description: 'Live OAuth sync — push invoices, partners, products, and warehouse data' },
    { name: 'Discuss', description: 'Slack-like messaging on every SO, PO, and receipt — including federated partner threads' },
    { name: 'Webhooks & Exports', description: 'Scheduled CSV/XLSX/JSON exports and REST import API for external systems' },
    { name: 'Reorder Agent', description: 'AI emails suppliers for quotes and drafts purchase orders from reorder alerts' }
  ];

  return (
    <section id="integration" className="integration core-section">
      <div className="integration-container">
        <div className="integration-header">
          <h2 className="integration-title">Built for Distributors & Wholesalers</h2>
          <p className="integration-subtitle">
            Outgrew spreadsheets but don't want a multi-year ERP rollout? Neoshift Core gives trading
            companies in Indonesia and Southeast Asia a connected back office — with AI that learns your naming conventions.
          </p>
        </div>

        <div className="integration-content">
          <div className="integration-steps">
            <h3>Suggested Rollout Path</h3>
            <div className="steps-grid">
              {rolloutSteps.map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">{step.icon}</div>
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="integration-options">
            <h3>Modules & Integrations</h3>
            <div className="options-grid">
              {modules.map((mod, index) => (
                <div key={index} className="option-card">
                  <h4 className="option-title">{mod.name}</h4>
                  <p className="option-description">{mod.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flow-system">
            <div className="flow-content">
              <h3>Three Apps, One Connected Platform</h3>
              <p>
                Tenant staff use the Client app for the full back office. Warehouse floor staff use
                the mobile PWA for pick, put-away, and stock lookup. Neoshift operators provision
                companies and toggle modules per tenant from the Admin app.
              </p>
              <ul className="flow-benefits">
                <li>Client app — sales, finance, ops, and warehouse managers</li>
                <li>PWA — offline-capable floor operations with push notifications</li>
                <li>Modules enabled per company — no bloated menus</li>
                <li>Start in Neoshift; push to Accurate when you're ready</li>
              </ul>
            </div>
            <div className="flow-visual">
              <div className="flow-diagram">
                <div className="flow-block input">Master Data</div>
                <div className="flow-arrow">→</div>
                <div className="flow-block process">Orders &amp; WMS</div>
                <div className="flow-arrow">→</div>
                <div className="flow-block output">Invoice &amp; AP</div>
              </div>
            </div>
          </div>
        </div>

        <div className="integration-cta">
          <h3>See Neoshift Core in Action</h3>
          <p>Launch your workspace or book a demo — we'll walk through AI imports, pricing, and warehouse workflows on your use cases.</p>
          <div className="cta-buttons">
            <a
              href="https://core.neoshift.ai"
              className="cta-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Launch Neoshift Core
            </a>
            <button className="cta-secondary" onClick={onContactClick}>Book a Demo</button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ========================================
   ETL Integration Section
   ======================================== */
const ETLSection = ({ onContactClick }) => {
  const pocSteps = [
    {
      step: '01',
      title: 'Discovery Call',
      description: 'A 30-minute call to understand your specific workflows, pain points, and the documents you process daily.',
      icon: '📞'
    },
    {
      step: '02',
      title: 'Share Sample Documents',
      description: 'Provide sample documents — POs, invoices, or any format. Confidentiality guaranteed. We handle your data with care.',
      icon: '📄'
    },
    {
      step: '03',
      title: 'See Your Free POC',
      description: 'We build a working demo on your actual data so you can see the extraction, validation, and sync results firsthand.',
      icon: '🎯'
    },
    {
      step: '04',
      title: 'Go-Live in 4 Weeks',
      description: 'From kick-off to production in 4 weeks — including model training, integration testing, UAT, and deployment.',
      icon: '🚀'
    }
  ];

  const operations = [
    { name: 'PO Processing', description: 'Automate purchase order intake, extraction, and ERP entry' },
    { name: 'Invoice Processing', description: 'Extract, validate, and route invoices for faster approvals' },
    { name: 'DO/POD Processing', description: 'Process delivery orders and proof-of-delivery documents' },
    { name: 'Payment Processing', description: 'Reconcile payments and sync with your financial systems' },
    { name: 'RFQ & Quote Processing', description: 'Automate quote extraction and comparison workflows' },
    { name: 'Contract Management', description: 'Extract key terms and track obligations automatically' }
  ];

  return (
    <section id="integration" className="integration">
      <div className="integration-container">
        <div className="integration-header">
          <h2 className="integration-title">From Kick-Off to Go-Live in 4 Weeks</h2>
          <p className="integration-subtitle">
            We prove our value upfront with a complimentary Proof-of-Concept on your actual data.
            No risk, no commitment — just results.
          </p>
        </div>

        <div className="integration-content">
          <div className="integration-steps">
            <h3>Start Risk-Free</h3>
            <div className="steps-grid">
              {pocSteps.map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">{step.icon}</div>
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="integration-options">
            <h3>B2B Operations We Automate</h3>
            <div className="options-grid">
              {operations.map((op, index) => (
                <div key={index} className="option-card">
                  <h4 className="option-title">{op.name}</h4>
                  <p className="option-description">{op.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flow-system">
            <div className="flow-content">
              <h3>Seamless AI Layer on Your Existing Stack</h3>
              <p>
                Our AI runs invisibly in the background of your current software and workflows.
                Your team keeps using familiar tools while Neoshift eliminates the manual,
                error-prone parts — and syncs data across all your systems automatically.
              </p>
              <ul className="flow-benefits">
                <li>No migration or workflow disruption</li>
                <li>Plug-and-play with your ERP, CRM, and WMS</li>
                <li>Start with 1 use case, then scale across operations</li>
                <li>70-95% reduction in manual data entry</li>
              </ul>
            </div>
            <div className="flow-visual">
              <div className="flow-diagram">
                <div className="flow-block input">Read &amp; Extract</div>
                <div className="flow-arrow">→</div>
                <div className="flow-block process">Validate</div>
                <div className="flow-arrow">→</div>
                <div className="flow-block output">Process &amp; Sync</div>
              </div>
            </div>
          </div>
        </div>

        <div className="integration-cta">
          <h3>See the Accuracy on Your Data — Risk Free</h3>
          <p>Let us show you how Neoshift performs on your actual documents with a complimentary Proof-of-Concept.</p>
          <div className="cta-buttons">
            <button className="cta-primary" onClick={onContactClick}>Get Your Free POC</button>
            <button className="cta-secondary" onClick={onContactClick}>Book a Discovery Call</button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ========================================
   BI Section – Data Studio, Use Cases, CTA
   ======================================== */
const BISection = ({ onContactClick }) => {
  const biSteps = [
    {
      step: '01',
      title: 'Connect Your Data',
      description: 'Import data from CSV, Excel, REST APIs, PostgreSQL, MySQL, or Google Sheets. Set up scheduled syncs to keep everything current.',
      icon: '🔌'
    },
    {
      step: '02',
      title: 'Build Views with AI',
      description: 'Let our AI discover relationships between your data sources. Join tables, add calculated columns, and create unified views — no SQL needed.',
      icon: '🤖'
    },
    {
      step: '03',
      title: 'Ask Questions',
      description: 'Use natural language to query your data. Ask anything from "What were last month\'s sales?" to "Show me customer churn trends."',
      icon: '💬'
    },
    {
      step: '04',
      title: 'Share Dashboards',
      description: 'Build interactive dashboards with drag-and-drop, then share them with your team using role-based access controls.',
      icon: '📊'
    }
  ];

  const useCases = [
    {
      icon: '💰',
      title: 'Sales Analytics',
      description: 'Track revenue, pipeline performance, and sales team metrics in real-time. Forecast future performance with AI.'
    },
    {
      icon: '📈',
      title: 'Marketing Intelligence',
      description: 'Measure campaign ROI, customer acquisition costs, and channel performance across all your marketing efforts.'
    },
    {
      icon: '🎯',
      title: 'Product Analytics',
      description: 'Monitor user engagement, feature adoption, and retention metrics. Identify what drives growth.'
    },
    {
      icon: '📋',
      title: 'Financial Reporting',
      description: 'Automate P&L statements, cash flow analysis, and budget vs. actuals reports with always-current data.'
    }
  ];

  const dataStudioFeatures = [
    'Connect and combine tables without writing SQL',
    'Create views by joining tables and adding calculated columns',
    'Use AI to automatically discover relationships between data sources',
    'Deploy views directly into BigQuery for analysis and dashboards'
  ];

  return (
    <section id="integration" className="integration bi-section">
      <div className="integration-container">
        {/* How It Works */}
        <div className="integration-header">
          <h2 className="integration-title">How Neoshift BI Works</h2>
          <p className="integration-subtitle">
            From connecting your first data source to sharing dashboards with your team —
            get up and running in minutes, not months.
          </p>
        </div>

        <div className="integration-content">
          <div className="integration-steps">
            <div className="steps-grid">
              {biSteps.map((step, index) => (
                <div key={index} className="step-card">
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon">{step.icon}</div>
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Data Studio */}
          <div className="flow-system">
            <div className="flow-content">
              <h3>Data Studio</h3>
              <p>
                Your AI-powered data workshop. Connect, combine, and transform data from
                any source into actionable views — all without writing a single line of SQL.
              </p>
              <ul className="flow-benefits">
                {dataStudioFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
            <div className="flow-visual">
              <div className="flow-diagram">
                <div className="flow-block input">Raw Tables</div>
                <div className="flow-arrow">→</div>
                <div className="flow-block process">AI Data Studio</div>
                <div className="flow-arrow">→</div>
                <div className="flow-block output">Unified Views</div>
              </div>
            </div>
          </div>

          {/* Use Cases */}
          <div className="bi-use-cases">
            <h3>Built for Every Team</h3>
            <div className="use-cases-grid">
              {useCases.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <div className="use-case-icon">{useCase.icon}</div>
                  <h4 className="use-case-title">{useCase.title}</h4>
                  <p className="use-case-description">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="integration-cta">
          <h3>Start Making Data-Driven Decisions</h3>
          <p>Launch Neoshift BI and let AI transform the way your team understands data.</p>
          <div className="cta-buttons">
            <a
              href="https://bi.neoshift.ai"
              className="cta-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Launch Neoshift BI
            </a>
            <button className="cta-secondary" onClick={onContactClick}>Contact Sales</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Integration;
